/**
 * Smart Suggestions Service - Phase 5
 * 
 * Generates intelligent task suggestions based on:
 * - User spending patterns
 * - Budget status
 * - Savings goal progress
 * - Time-based triggers (end of month, payday, etc.)
 * - Historical user behavior
 * 
 * SAFETY: Additive service, no breaking changes.
 * 
 * @module suggestionsService
 */

// ============================================================================
// SUGGESTION TYPES
// ============================================================================

export const SUGGESTION_TYPE = {
  BUDGET_REVIEW: 'budget_review',
  SAVINGS_BOOST: 'savings_boost',
  SPENDING_ALERT: 'spending_alert',
  BILL_REMINDER: 'bill_reminder',
  WEEKLY_REVIEW: 'weekly_review',
  MONTH_END: 'month_end',
  GOAL_PROGRESS: 'goal_progress',
  CATEGORY_OVERSPEND: 'category_overspend',
  PAYDAY_PLANNING: 'payday_planning',
};

export const SUGGESTION_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
};

// ============================================================================
// SUGGESTION TEMPLATES
// ============================================================================

const SUGGESTION_TEMPLATES = {
  // Budget-related suggestions
  BUDGET_OVER_80: {
    type: SUGGESTION_TYPE.SPENDING_ALERT,
    title: 'Review {category} spending',
    description: 'You\'ve used {percentage}% of your {category} budget. Review recent transactions.',
    icon: '⚠️',
    priority: SUGGESTION_PRIORITY.HIGH,
    taskTemplate: {
      title: 'Review {category} spending',
      description: 'Budget is at {percentage}%. Check if any expenses can be reduced.',
      isFinancialTask: true,
      priority: 'high',
    },
  },
  
  BUDGET_ALMOST_FULL: {
    type: SUGGESTION_TYPE.BUDGET_REVIEW,
    title: '{category} budget nearly full',
    description: 'Only {remaining} left in {category}. Plan remaining spend carefully.',
    icon: '🔴',
    priority: SUGGESTION_PRIORITY.URGENT,
    taskTemplate: {
      title: 'Manage remaining {category} budget',
      description: 'Only {remaining} left. Decide on essential vs optional expenses.',
      isFinancialTask: true,
      priority: 'high',
    },
  },

  BUDGET_ON_TRACK: {
    type: SUGGESTION_TYPE.BUDGET_REVIEW,
    title: 'Great job on {category}!',
    description: 'You\'re on track with {category} spending at {percentage}%.',
    icon: '✅',
    priority: SUGGESTION_PRIORITY.LOW,
    taskTemplate: null, // No task needed for positive feedback
  },

  // Savings suggestions
  SAVINGS_BEHIND: {
    type: SUGGESTION_TYPE.SAVINGS_BOOST,
    title: 'Boost your savings',
    description: 'Your "{goalName}" goal is {percentage}% behind schedule.',
    icon: '🐷',
    priority: SUGGESTION_PRIORITY.MEDIUM,
    taskTemplate: {
      title: 'Contribute to {goalName}',
      description: 'Add to your savings to get back on track.',
      isFinancialTask: true,
      priority: 'medium',
    },
  },

  SAVINGS_ALMOST_THERE: {
    type: SUGGESTION_TYPE.GOAL_PROGRESS,
    title: 'Almost there!',
    description: '"{goalName}" is {percentage}% complete. Just {remaining} to go!',
    icon: '🎯',
    priority: SUGGESTION_PRIORITY.MEDIUM,
    taskTemplate: {
      title: 'Final push for {goalName}',
      description: 'Only {remaining} left to reach your goal!',
      isFinancialTask: true,
      priority: 'high',
    },
  },

  // Time-based suggestions
  WEEKLY_REVIEW: {
    type: SUGGESTION_TYPE.WEEKLY_REVIEW,
    title: 'Weekly money check-in',
    description: 'It\'s {dayName}! Time to review your weekly spending.',
    icon: '📊',
    priority: SUGGESTION_PRIORITY.MEDIUM,
    taskTemplate: {
      title: 'Weekly spending review',
      description: 'Check spending across all categories and adjust if needed.',
      isFinancialTask: true,
      priority: 'medium',
    },
  },

  MONTH_END_CLOSE: {
    type: SUGGESTION_TYPE.MONTH_END,
    title: 'Month-end planning',
    description: 'Only {daysLeft} days left in the month. Time for final review.',
    icon: '📅',
    priority: SUGGESTION_PRIORITY.HIGH,
    taskTemplate: {
      title: 'Month-end budget review',
      description: 'Review all categories and prepare for next month.',
      isFinancialTask: true,
      priority: 'high',
    },
  },

  NEW_MONTH_START: {
    type: SUGGESTION_TYPE.PAYDAY_PLANNING,
    title: 'Fresh month, fresh start!',
    description: 'Set up your budgets and goals for the new month.',
    icon: '🌟',
    priority: SUGGESTION_PRIORITY.HIGH,
    taskTemplate: {
      title: 'Set up monthly budgets',
      description: 'Review last month and set this month\'s budget allocations.',
      isFinancialTask: true,
      priority: 'high',
    },
  },

  // Category-specific suggestions
  GROCERY_OVERSPEND: {
    type: SUGGESTION_TYPE.CATEGORY_OVERSPEND,
    title: 'Grocery spending high',
    description: 'Groceries are {percentage}% over typical. Review recent purchases.',
    icon: '🛒',
    priority: SUGGESTION_PRIORITY.MEDIUM,
    taskTemplate: {
      title: 'Review grocery spending',
      description: 'Check for unnecessary purchases or price increases.',
      isFinancialTask: true,
      priority: 'medium',
    },
  },

  DINING_FREQUENT: {
    type: SUGGESTION_TYPE.SPENDING_ALERT,
    title: 'Dining out frequently',
    description: 'You\'ve dined out {count} times this week. Consider cooking at home.',
    icon: '🍽️',
    priority: SUGGESTION_PRIORITY.LOW,
    taskTemplate: {
      title: 'Plan home-cooked meals',
      description: 'Try cooking at home this week to save money.',
      isFinancialTask: false,
      priority: 'low',
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Replace placeholders in template strings
 */
function interpolate(template, values) {
  let result = template;
  Object.entries(values).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{${key}}`, 'g'), value);
  });
  return result;
}

/**
 * Get the day name (e.g., "Sunday")
 */
function getDayName(date = new Date()) {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

/**
 * Get days remaining in the current month
 */
function getDaysLeftInMonth() {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return lastDay.getDate() - today.getDate();
}

/**
 * Check if today is the first few days of the month
 */
function isStartOfMonth(daysThreshold = 3) {
  return new Date().getDate() <= daysThreshold;
}

/**
 * Check if today is near the end of the month
 */
function isEndOfMonth(daysThreshold = 5) {
  return getDaysLeftInMonth() <= daysThreshold;
}

/**
 * Check if today is a typical "review day" (Sunday or Monday)
 */
function isReviewDay() {
  const day = new Date().getDay();
  return day === 0 || day === 1; // Sunday or Monday
}

// ============================================================================
// MAIN SUGGESTION GENERATORS
// ============================================================================

/**
 * Generate suggestions based on budget status
 * 
 * @param {Array} envelopes - Array of envelope/budget objects
 * @param {string} currencySymbol - Currency symbol for display
 * @returns {Array} Array of suggestion objects
 */
export function generateBudgetSuggestions(envelopes, currencySymbol = '£') {
  const suggestions = [];

  if (!envelopes || envelopes.length === 0) return suggestions;

  envelopes.forEach(envelope => {
    const { name, allocated, spent } = envelope;
    if (!allocated || allocated <= 0) return;

    const percentage = Math.round((spent / allocated) * 100);
    const remaining = allocated - spent;

    if (percentage >= 100) {
      // Over budget
      suggestions.push(createSuggestion(
        SUGGESTION_TEMPLATES.BUDGET_ALMOST_FULL,
        { 
          category: name, 
          percentage, 
          remaining: `${currencySymbol}${Math.abs(remaining).toFixed(2)}` 
        }
      ));
    } else if (percentage >= 80) {
      // Near limit
      suggestions.push(createSuggestion(
        SUGGESTION_TEMPLATES.BUDGET_OVER_80,
        { category: name, percentage }
      ));
    }
  });

  return suggestions;
}

/**
 * Generate suggestions based on savings goals
 * 
 * @param {Array} goals - Array of savings goal objects
 * @param {string} currencySymbol - Currency symbol for display
 * @returns {Array} Array of suggestion objects
 */
export function generateSavingsSuggestions(goals, currencySymbol = '£') {
  const suggestions = [];

  if (!goals || goals.length === 0) return suggestions;

  goals.forEach(goal => {
    const { name, targetAmount, currentAmount, targetDate } = goal;
    if (!targetAmount) return;

    const percentage = Math.round((currentAmount / targetAmount) * 100);
    const remaining = targetAmount - currentAmount;

    // Check if behind schedule (if target date exists)
    if (targetDate) {
      const now = new Date();
      const target = new Date(targetDate);
      const totalDays = (target - new Date(goal.createdAt || now)) / (1000 * 60 * 60 * 24);
      const elapsedDays = (now - new Date(goal.createdAt || now)) / (1000 * 60 * 60 * 24);
      const expectedPercentage = Math.round((elapsedDays / totalDays) * 100);

      if (percentage < expectedPercentage - 10) {
        suggestions.push(createSuggestion(
          SUGGESTION_TEMPLATES.SAVINGS_BEHIND,
          { 
            goalName: name, 
            percentage: expectedPercentage - percentage,
            remaining: `${currencySymbol}${remaining.toFixed(2)}`
          }
        ));
      }
    }

    // Almost there suggestion
    if (percentage >= 80 && percentage < 100) {
      suggestions.push(createSuggestion(
        SUGGESTION_TEMPLATES.SAVINGS_ALMOST_THERE,
        { 
          goalName: name, 
          percentage,
          remaining: `${currencySymbol}${remaining.toFixed(2)}`
        }
      ));
    }
  });

  return suggestions;
}

/**
 * Generate time-based suggestions
 * 
 * @returns {Array} Array of suggestion objects
 */
export function generateTimeSuggestions() {
  const suggestions = [];

  // Start of month
  if (isStartOfMonth()) {
    suggestions.push(createSuggestion(
      SUGGESTION_TEMPLATES.NEW_MONTH_START,
      {}
    ));
  }

  // End of month
  if (isEndOfMonth()) {
    suggestions.push(createSuggestion(
      SUGGESTION_TEMPLATES.MONTH_END_CLOSE,
      { daysLeft: getDaysLeftInMonth() }
    ));
  }

  // Weekly review day
  if (isReviewDay()) {
    suggestions.push(createSuggestion(
      SUGGESTION_TEMPLATES.WEEKLY_REVIEW,
      { dayName: getDayName() }
    ));
  }

  return suggestions;
}

/**
 * Create a suggestion object from a template
 */
function createSuggestion(template, values) {
  const id = `${template.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  return {
    id,
    type: template.type,
    title: interpolate(template.title, values),
    description: interpolate(template.description, values),
    icon: template.icon,
    priority: template.priority,
    taskTemplate: template.taskTemplate ? {
      ...template.taskTemplate,
      title: interpolate(template.taskTemplate.title, values),
      description: interpolate(template.taskTemplate.description, values),
    } : null,
    createdAt: new Date().toISOString(),
    values, // Keep original values for later use
  };
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

/**
 * Generate all suggestions based on current state
 * 
 * @param {Object} context - Context containing envelopes, goals, etc.
 * @param {Array} context.envelopes - Budget envelopes
 * @param {Array} context.goals - Savings goals
 * @param {string} context.currencySymbol - Currency symbol
 * @returns {Array} Sorted array of suggestions
 */
export function generateAllSuggestions(context = {}) {
  const { envelopes = [], goals = [], currencySymbol = '£' } = context;

  const allSuggestions = [
    ...generateBudgetSuggestions(envelopes, currencySymbol),
    ...generateSavingsSuggestions(goals, currencySymbol),
    ...generateTimeSuggestions(),
  ];

  // Sort by priority
  const priorityOrder = {
    [SUGGESTION_PRIORITY.URGENT]: 0,
    [SUGGESTION_PRIORITY.HIGH]: 1,
    [SUGGESTION_PRIORITY.MEDIUM]: 2,
    [SUGGESTION_PRIORITY.LOW]: 3,
  };

  return allSuggestions.sort((a, b) => 
    priorityOrder[a.priority] - priorityOrder[b.priority]
  );
}

/**
 * Convert a suggestion to a task object ready for creation
 * 
 * @param {Object} suggestion - Suggestion object with taskTemplate
 * @param {string} listId - Target list ID
 * @returns {Object|null} Task object or null if no template
 */
export function suggestionToTask(suggestion, listId) {
  if (!suggestion.taskTemplate) return null;

  const template = suggestion.taskTemplate;
  
  return {
    title: template.title,
    description: template.description || '',
    isFinancialTask: template.isFinancialTask || false,
    priority: template.priority || 'medium',
    listId,
    dueDate: new Date().toISOString(), // Due today by default
    source: 'suggestion',
    suggestionType: suggestion.type,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  SUGGESTION_TYPE,
  SUGGESTION_PRIORITY,
  generateBudgetSuggestions,
  generateSavingsSuggestions,
  generateTimeSuggestions,
  generateAllSuggestions,
  suggestionToTask,
};
